/**
 * O CPF OU CNPJ ENQUANTO SE DIGITA (pente fino de 31/10/2026).
 *
 * O campo aceitava "12345678000195" e só mostrava a máscara na lista, depois
 * de gravado. Agora a máscara entra a cada tecla: até onze dígitos é CPF
 * (000.000.000-00), de doze em diante é CNPJ (00.000.000/0000-00). Quem confere
 * o número contra o papel lê os grupos que o papel tem.
 *
 * O que vai ao banco não muda: `corpoDe` continua mandando só os dígitos.
 */

const soDigitos = (texto: string) => texto.replace(/\D/g, '');

/** O texto do campo com a máscara do que já foi digitado, no máximo 14 dígitos. */
export function documentoAoDigitar(texto: string): string {
  const d = soDigitos(texto).slice(0, 14);
  if (d.length <= 11) {
    /* CPF: 000.000.000-00, montado pelo que já existe. */
    const partes = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 9)].filter((p) => p !== '');
    const corpo = partes.join('.');
    return d.length > 9 ? `${corpo}-${d.slice(9)}` : corpo;
  }
  /* CNPJ: 00.000.000/0000-00. */
  const inicio = [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8)].join('.');
  const filial = d.slice(8, 12);
  const dv = d.slice(12);
  return `${inicio}/${filial}${dv === '' ? '' : `-${dv}`}`;
}

/**
 * Os dígitos verificadores conferem? `null` quando ainda não é CPF (11) nem
 * CNPJ (14): aí quem fala é a regra do tamanho, em `campos.ts`.
 *
 * NÃO é regra nova do servidor, e não impede gravar: é o aviso do dígito
 * trocado, que o servidor aceitaria e que faria o mesmo fornecedor entrar duas
 * vezes, com um número que não existe.
 */
export function verificadoresConferem(texto: string): boolean | null {
  const d = soDigitos(texto);
  if (d.length === 11) return cpfConfere(d);
  if (d.length === 14) return cnpjConfere(d);
  return null;
}

function cpfConfere(d: string): boolean {
  if (/^(\d)\1{10}$/.test(d)) return false;
  const digito = (ate: number) => {
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += Number(d[i]) * (ate + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return digito(9) === Number(d[9]) && digito(10) === Number(d[10]);
}

function cnpjConfere(d: string): boolean {
  if (/^(\d)\1{13}$/.test(d)) return false;
  const digito = (ate: number) => {
    const pesos = ate === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = pesos.reduce((total, peso, i) => total + Number(d[i]) * peso, 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return digito(12) === Number(d[12]) && digito(13) === Number(d[13]);
}
